import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2 } from 'lucide-react';
import { userService } from '../../_services';

function CampaignPopup({
    text,
    closeCampaignPopup,
    openManageGroup,
    SelectedGroupNumber,
    MaxComm,
    cPagin,
    cSearch,
    reopenFlag,
    allData,
    userType,
    chkUser,
    disEmail
}) {
    const queryClient = useQueryClient();
    const searchTimeoutRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [submitDisabled, setSubmitDisabled] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [userDataList, setUserDataList] = useState([]);
    const [searchDisplay, setSearchDisplay] = useState([]);
    const [renderList, setRenderList] = useState([]);
    const [campaignList, setCampaignList] = useState([]);
    const [currentEmail, setCurrentEmail] = useState('');
    
    // Local form state - separate from API data
    const [emailId, setEmailId] = useState('');
    const [distributorName, setDistributorName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [distributorCommission, setDistributorCommission] = useState('');
    
    // Field errors
    const [disNameError, setDisNameError] = useState('');
    const [disEmailError, setDisEmailError] = useState('');
    const [disPhoneError, setDisPhoneError] = useState('');
    const [disCommissionError, setDisCommissionError] = useState('');

    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    }, []);

    const fetchUserList = useCallback(async () => {
        if (!userdata) return;

        try {
            const response = await fetch('/api/ws/groupnamedetails', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'X-Browser-Url': window.location.href 
                },
                body: JSON.stringify({
                    WhName: userdata.paramsname,
                    GroupNumber: SelectedGroupNumber
                })
            });

            const apidata = await response.json();

            if (apidata?.length > 0 && apidata[0].distributor_UserId !== "0") {
                const updatedData = apidata.map(a => ({
                    ...a,
                    distributorCommission: a.distributorCommission === "0" ? "1.75" : a.distributorCommission,
                    userType: userType
                }));
                setUserDataList(updatedData);
                setCurrentEmail(updatedData[0]?.emailId || '');
                // Initialize local form state
                setEmailId(updatedData[0]?.emailId || '');
                setDistributorName(updatedData[0]?.distributorName || '');
                setPhoneNumber(updatedData[0]?.phoneNumber || '');
                setDistributorCommission(updatedData[0]?.distributorCommission || '');
                return updatedData;
            } else {
                const updatedData = apidata.map(a => ({
                    ...a,
                    distributorCommission: a.distributorCommission === "0" ? "1.75" : a.distributorCommission,
                    userType: userType
                }));
                setUserDataList(updatedData);
                setCurrentEmail(updatedData[0]?.emailId || '');
                // Initialize local form state
                setEmailId(updatedData[0]?.emailId || '');
                setDistributorName(updatedData[0]?.distributorName || '');
                setPhoneNumber(updatedData[0]?.phoneNumber || '');
                setDistributorCommission(updatedData[0]?.distributorCommission || '');
                return updatedData;
            }
        } catch (err) {
            return null;
        }
    }, [userdata, SelectedGroupNumber, userType]);

    useEffect(() => {
        const loadData = async () => {
            const data = await fetchUserList();
            
            // If chkUser is false and we have data, search for distributor logs
            if (data && chkUser === 'false' && disEmail) {
                searchDistributorLogs(disEmail);
            }
        };
        
        loadData();
        
        // Enable/disable submit based on user
        const timer = setTimeout(() => {
            if (userdata?.paramsname?.toLowerCase() === 'patient rx discount') {
                setSubmitDisabled(true);
            } else {
                setSubmitDisabled(false);
            }
        }, 700);
        
        return () => clearTimeout(timer);
    // Only run on mount - functions are stable enough for our needs
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const searchDistributorLogs = useCallback(async (emailId) => {
        try {
            const apidata = await userService.SearchEmailCampaignType(
                emailId,
                userType,
                userdata.wholesaler_Id.toString()
            );

            if (emailId?.length > 0) {
                if (chkUser === 'false') {
                    setCampaignList(apidata);
                } else {
                    setSearchDisplay(apidata);
                    setRenderList(apidata);
                }
            }
        } catch (err) {
            setError('Oops! Something went wrong. Try Again.');
        }
    }, [userType, userdata, chkUser]);

    const handleEmailKeyUp = useCallback((e) => {
        const emailValue = e.target.value;
        
        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        // Only search if email has at least 2 characters
        if (emailValue.length >= 2) {
            // Debounce: only search after user stops typing for 300ms
            searchTimeoutRef.current = setTimeout(() => {
                searchDistributorLogs(emailValue);
            }, 300);
        }
    }, [searchDistributorLogs]);

    const handleFieldChange = useCallback((field, value) => {
        setError('');

        // Update local state only
        if (field === 'emailId') {
            setEmailId(value);
            setDisEmailError('');
            // If email changed from saved email, clear other fields
            if (currentEmail && value !== currentEmail) {
                setDistributorName('');
                setPhoneNumber('');
                setDistributorCommission('');
                setCampaignList([]);
            }
        }
        if (field === 'distributorName') {
            setDistributorName(value);
            setDisNameError('');
        }
        if (field === 'phoneNumber') {
            setPhoneNumber(value);
            setDisPhoneError('');
        }
        if (field === 'distributorCommission') {
            setDistributorCommission(value);
            setDisCommissionError('');
        }
    }, [currentEmail]);

    const handleSearchItemClick = useCallback((item) => {
        // Update local form state
        setEmailId(item.emailId);
        setDistributorName(item.distributorName);
        setPhoneNumber(item.phoneNumber);
        setDistributorCommission(item.distributorCommission);
        
        const filteredList = renderList.filter(i => i.emailId === item.emailId);
        setSearchDisplay([]);
        setCampaignList(filteredList);
        setCurrentEmail(item.emailId);
        setDisNameError('');
        setDisEmailError('');
    }, [renderList]);

    const validateDistributor = useCallback(() => {
        let isValid = true;
        
        setDisNameError('');
        setDisEmailError('');
        setDisCommissionError('');
        setDisPhoneError('');
        setError('');
        setLoading(true);
        setSubmitDisabled(true);

        // Name validation
        if (!distributorName) {
            setDisNameError('Name required');
            isValid = false;
        } else if (distributorName.indexOf('.') !== -1) {
            setDisNameError('Dot not allowed');
            isValid = false;
        } else if (!/^\w+(\s\w+)*$/.test(distributorName)) {
            setDisNameError('Space not allowed');
            isValid = false;
        }

        // Email validation
        if (!emailId) {
            setDisEmailError('Email required');
            isValid = false;
        } else if (emailId.indexOf(' ') >= 0) {
            setDisEmailError('Space not allowed');
            isValid = false;
        } else if (!userService.ValidateEmail(emailId)) {
            setDisEmailError('Invalid email');
            isValid = false;
        }

        // Commission validation
        if (!distributorCommission) {
            setDisCommissionError('Commission required');
            isValid = false;
        } else if (distributorCommission.indexOf(' ') >= 0) {
            setDisCommissionError('Space not allowed');
            isValid = false;
        } else if (!userService.validateCurrency(distributorCommission)) {
            setDisCommissionError('Invalid Commission');
            isValid = false;
        }

        if (!isValid) {
            setLoading(false);
            setSubmitDisabled(false);
        }

        return isValid;
    }, [emailId, distributorName, distributorCommission]);

    const checkCommissionExceed = useCallback(() => {
        const usercomm = parseFloat(distributorCommission);
        return usercomm <= parseFloat(MaxComm) || userDataList[0]?.IsRuleType;
    }, [distributorCommission, MaxComm, userDataList]);

    const handleSaveSubmit = useCallback(async () => {
        if (userdata?.paramsname?.toLowerCase() === 'patient rx discount') {
            setError('Patient rx discount not allowed');
            setLoading(false);
            setSubmitDisabled(true);
            return;
        }

        if (!validateDistributor()) return;

        if (!checkCommissionExceed()) {
            setError('The total commission for this group number exceeds the maximum allowed commission');
            setLoading(false);
            setSubmitDisabled(false);
            return;
        }

        // Sync local form state back to userDataList before saving
        const updatedUserDataList = userDataList.map((item, index) => {
            if (index === 0) {
                return {
                    ...item,
                    emailId,
                    distributorName,
                    phoneNumber,
                    distributorCommission
                };
            }
            return item;
        });

        try {
            const apidata = await userService.ProcessUserCampaign(
                JSON.stringify(updatedUserDataList),
                userdata.wholesaler_Id.toString(),
                SelectedGroupNumber,
                MaxComm
            );

            if (apidata?.[0]?.code === "1") {
                setLoading(false);
                setSuccessMessage(apidata[0].message);
                setError('');
                setSubmitDisabled(true);
                
                // Invalidate cache so ManageGroupNumber refetches fresh data
                queryClient.invalidateQueries({
                    queryKey: ['manageGroupUsers'],
                    exact: false
                });
                
                // Close popup after a short delay to show success message
                setTimeout(() => {
                    closeCampaignPopup?.();
                }, 1500);
            } else {
                setLoading(false);
                setError(apidata?.[0]?.message || 'Whoops, something went wrong. Please try again');
                setSuccessMessage('');
                setSubmitDisabled(false);
            }
        } catch (err) {
            setLoading(false);
            setError('Oops! Something went wrong. Try Again.');
            setSubmitDisabled(false);
        }
    }, [userdata, userDataList, validateDistributor, checkCommissionExceed, SelectedGroupNumber, MaxComm, queryClient, closeCampaignPopup]);

    const handleBack = useCallback(() => {
        closeCampaignPopup?.();
        openManageGroup?.(SelectedGroupNumber, MaxComm, cPagin, cSearch, '', '', reopenFlag, allData);
    }, [closeCampaignPopup, openManageGroup, SelectedGroupNumber, MaxComm, cPagin, cSearch, reopenFlag, allData]);

    const handleOpenChange = useCallback((open) => {
        if (!open) {
            handleBack();
        }
    }, [handleBack]);

    return (
        <Dialog open={true} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{text}</DialogTitle>
                </DialogHeader>

                <div className="flex flex-col gap-4">
                    {userDataList[0] ? (
                        <div className="space-y-4">
                            {/* Form Fields */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="flex flex-col gap-2 relative">
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        type="text"
                                        value={emailId}
                                        onChange={(e) => setEmailId(e.target.value)}
                                        onKeyUp={handleEmailKeyUp}
                                    />
                                    {disEmailError && (
                                        <p className="text-sm text-destructive mt-1">{disEmailError}</p>
                                    )}
                                    
                                    {/* Search Dropdown */}
                                    {searchDisplay.length > 0 && (
                                        <div className="absolute z-10 w-full mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-auto">
                                            {searchDisplay.map((item, index) => (
                                                <div
                                                    key={index}
                                                    className={`px-3 py-2 hover:bg-accent cursor-pointer ${index < searchDisplay.length - 1 ? 'border-b' : ''}`}
                                                    onClick={() => handleSearchItemClick(item)}
                                                >
                                                    {item.distributorName}
                                                    </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="flex flex-col gap-2">
                                    <Label htmlFor="name">Name</Label>
                                    <Input
                                        id="name"
                                        type="text"
                                        value={distributorName}
                                        onChange={(e) => setDistributorName(e.target.value)}
                                    />
                                    {disNameError && (
                                        <p className="text-sm text-destructive mt-1">{disNameError}</p>
                                    )}
                                        </div>

                                <div className="flex flex-col gap-2">
                                    <Label htmlFor="phone">Phone Number</Label>
                                    <Input
                                        id="phone"
                                        type="text"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.target.value)}
                                    />
                                    {disPhoneError && (
                                        <p className="text-sm text-destructive mt-1">{disPhoneError}</p>
                                    )}
                                        </div>
                                        </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="flex flex-col gap-2">
                                    <Label htmlFor="commission">Commission ($)</Label>
                                    <Input
                                        id="commission"
                                        type="text"
                                        value={distributorCommission}
                                        onChange={(e) => setDistributorCommission(e.target.value)}
                                    />
                                    {disCommissionError && (
                                        <p className="text-sm text-destructive mt-1">{disCommissionError}</p>
                                    )}
                                </div>
                            </div>

                            {/* Campaign List Table */}
                            {campaignList.length > 1 && (
                                <div className="rounded-md border max-h-[200px] overflow-auto mt-4">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Group Number</TableHead>
                                                <TableHead>Name</TableHead>
                                                <TableHead>Email</TableHead>
                                                <TableHead>Phone Number</TableHead>
                                                <TableHead>Commission ($)</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {campaignList.map((item, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{item.groupNumber}</TableCell>
                                                    <TableCell>{item.distributorName}</TableCell>
                                                    <TableCell>{item.emailId}</TableCell>
                                                    <TableCell>{item.phoneNumber}</TableCell>
                                                    <TableCell>{item.distributorCommission?.toFixed(2)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}

                            {/* Success Message */}
                            {successMessage && (
                                <div className="p-3 rounded-md bg-green-50 text-green-700 text-sm border border-green-200">
                                    {successMessage}
                                </div>
                            )}

                            {/* Error Message */}
                            {error && (
                                <p className="text-sm text-destructive">
                                    {error}
                                </p>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-4">
                            <p>Loading...</p>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button 
                        onClick={handleSaveSubmit}
                        disabled={submitDisabled || loading}
                    >
                        {loading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            'Save & Submit'
                        )}
                    </Button>
                    <Button variant="secondary" onClick={handleBack}>
                        Cancel
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        );
}

export default CampaignPopup;
